<!--
  Foto e allegati, condivisa da dettaglio prenotazione e dettaglio
  spostamento interno. Con `editable` si possono anche aggiungere ed eliminare
  allegati: il componente emette `upload` (il File scelto) e `remove`
  (l'allegato); a caricare e rileggere ci pensa la pagina. Quelli del driver
  non si eliminano: sono i documenti della corsa (DDT, firme).

  - `groups`: foto a gruppi [{ key, title, urls: [] }] (tratta, fase…)
  - `attachments`: allegati liberi del backend (AttachmentItemDto), ognuno con
    `url` pre-firmato: le immagini vanno in griglia, gli altri file in elenco.
  Gli URL pre-firmati scadono: la pagina li rilegge a ogni apertura.
-->
<template>
  <div
    v-loading="loading"
    class="mg"
  >
    <div
      v-if="editable"
      class="mg__bar"
    >
      <label
        class="mg__add"
        :class="{ 'is-busy': uploading }"
      >
        <input
          type="file"
          class="mg__input"
          :disabled="uploading"
          aria-label="Aggiungi un allegato"
          @change="onFile"
        >
        {{ uploading ? 'Carico…' : '＋ Aggiungi allegato' }}
      </label>
      <span class="mg__barHint">Documenti, PDF, foto: fino a {{ maxMb }} MB.</span>
    </div>

    <div
      v-for="g in groups"
      :key="g.key"
      class="mg__block"
    >
      <span class="mg__label">{{ g.title }}</span>
      <div class="mg__photos">
        <el-image
          v-for="(src, i) in g.urls"
          :key="i"
          :src="src"
          :preview-src-list="g.urls"
          :initial-index="i"
          preview-teleported
          fit="cover"
          class="mg__photo"
          lazy
        />
      </div>
    </div>

    <div
      v-if="attachments.length"
      class="mg__block"
    >
      <span class="mg__label">Allegati</span>
      <div
        v-if="imageAttachments.length"
        class="mg__photos"
      >
        <div
          v-for="(a, i) in imageAttachments"
          :key="a.id"
          class="mg__photoWrap"
        >
          <el-image
            :src="a.url"
            :preview-src-list="imageAttachmentUrls"
            :initial-index="i"
            preview-teleported
            fit="cover"
            class="mg__photo"
            lazy
          />
          <button
            v-if="removable(a)"
            type="button"
            class="mg__del mg__del--photo"
            :aria-label="`Elimina ${a.file_name}`"
            @click="$emit('remove', a)"
          >
            ×
          </button>
        </div>
      </div>
      <div
        v-for="a in fileAttachments"
        :key="a.id"
        class="mg__fileRow"
      >
        <a
          :href="a.url"
          target="_blank"
          rel="noopener"
          class="mg__file"
        >
          <span>📎</span>
          <span class="mg__fileName">{{ a.file_name }}</span>
          <span class="mg__fileMeta">{{ fileMeta(a) }}</span>
        </a>
        <button
          v-if="removable(a)"
          type="button"
          class="mg__del"
          :aria-label="`Elimina ${a.file_name}`"
          @click="$emit('remove', a)"
        >
          ×
        </button>
      </div>
    </div>

    <p
      v-if="!loading && !groups.length && !attachments.length"
      class="mg__empty"
    >
      {{ emptyText }}
    </p>
    <p
      v-if="error"
      class="mg__empty mg__empty--attn"
    >
      ⚠ {{ error }}
    </p>
  </div>
</template>

<script>
const isImageFile = (a) => (a.mime_type || '').startsWith('image/');

export default {
  name: 'MediaGallery',
  props: {
    groups: { type: Array, default: () => [] },
    attachments: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    emptyText: { type: String, default: 'Nessuna foto o allegato.' },
    editable: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    maxMb: { type: Number, default: 20 },
  },
  emits: ['upload', 'remove'],
  computed: {
    imageAttachments() { return this.attachments.filter(isImageFile); },
    imageAttachmentUrls() { return this.imageAttachments.map((a) => a.url); },
    fileAttachments() { return this.attachments.filter((a) => !isImageFile(a)); },
  },
  methods: {
    removable(a) {
      return this.editable && a.uploaded_by !== 'driver';
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (file) this.$emit('upload', file);
    },
    fileMeta(a) {
      const n = Number(a.size_bytes);
      const size = !n ? ''
        : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB`
          : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
      const who = a.uploaded_by === 'driver' ? 'dal driver'
        : a.uploaded_by === 'admin' ? 'dal gestionale' : '';
      const when = a.created_at
        ? new Date(a.created_at).toLocaleString('it-IT', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        })
        : '';
      return [size, who, when].filter(Boolean).join(' · ');
    },
  },
};
</script>

<style scoped>
.mg__block + .mg__block { margin-top: 16px; }
.mg__label {
  display: block; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.mg__photos {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px; margin-top: 8px;
}
.mg__photo {
  width: 100%; aspect-ratio: 1; border-radius: 8px; cursor: zoom-in;
  border: 1px solid var(--border-color); background: var(--bg-secondary);
}
.mg__file {
  display: flex; align-items: center; gap: 8px; margin-top: 8px;
  padding: 8px 10px; border: 1px solid var(--border-color); border-radius: 8px;
  font-size: 13px; color: var(--text-primary); text-decoration: none;
}
.mg__file:hover { border-color: var(--brand-violet); }
.mg__fileName { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mg__fileMeta { margin-left: auto; flex: none; font-size: 12px; color: var(--text-tertiary); }
.mg__bar { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.mg__add {
  display: inline-flex; align-items: center; padding: 6px 12px; border-radius: 6px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  font-size: 12.5px; font-weight: 600; color: var(--text-primary); cursor: pointer;
}
.mg__add:hover { border-color: var(--brand-violet); }
.mg__add:focus-within { outline: 2px solid var(--brand-violet); outline-offset: 2px; }
.mg__add.is-busy { opacity: 0.6; cursor: progress; }
.mg__input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.mg__barHint { font-size: 11.5px; color: var(--text-tertiary); }
.mg__photoWrap { position: relative; }
.mg__fileRow { display: flex; align-items: center; gap: 6px; }
.mg__fileRow .mg__file { flex: 1; min-width: 0; }
.mg__del {
  flex: none; width: 28px; height: 28px; margin-top: 8px; border-radius: 6px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  color: var(--text-tertiary); font-size: 16px; line-height: 1; cursor: pointer;
}
.mg__del:hover { color: var(--state-danger, #b91c1c); border-color: currentColor; }
.mg__del--photo { position: absolute; top: 4px; right: 4px; margin: 0; width: 24px; height: 24px; }
.mg__empty { margin: 0; font-size: 12.5px; color: var(--text-tertiary); line-height: 1.5; }
.mg__empty--attn { margin-top: 8px; color: var(--state-attn); }
</style>
